/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore } from 'pinia'
import { factory } from '../../../factory-api/factory'
import type { IStep } from '../../interfaces/Interfaces'
import router from '@/router'
import { convertPropertiesToLowercase } from '@/application/helpers/convertPropNames'
import { useLocalizationStore } from '@/stores'
import { FactoryBuilder } from '../../../factory-api/FactoryBuilder'

interface GeneralTranslations {
  [key: string]: string
}

export const useOnboardingStore = defineStore('onboarding', () => {
  const route = useRoute()
  const steps = ref<IStep[]>([]) //<IStep[]>
  const dictionaries = ref<{ [key: string]: string }>({})
  const authorized: Ref<boolean> = ref(false)
  const initialized: Ref<boolean> = ref(false)
  const loadingState = ref<boolean>(true)
  const causeOfTreatment = ref<string>('')
  const t = ref<GeneralTranslations>()
  const currentStepType = ref<string | undefined>(undefined)
  const taeCoverage = ref<boolean>()
  const localizationStore = useLocalizationStore()
  const atRisk = ref<string>('')
  const businessUnitLogo = ref<string>('')
  const customerLogo = ref<string>('')
  const onboardingBasePath = ref<string>('')

  const init = async () => {
    console.log('init onboarding!')
    // Usable link for FOCUS dev: http://onboarding.localhost.dk:5173/690edb92-a387-ef11-ac20-7c1e524dc84c?token=7gbr4ooqan6y4kne
    // QA link example: https://onboarding.localhost.dk:5173/f5702fb7-8cad-ef11-b8e8-6045bd990ac4?token=7dpkirpnt5ckw5la

    // Important to await until the router is ready - else we cant get the final URL
    await router.isReady()
    const programmeId = route.params.programmeId as string
    const token = route.query.token as string
    // const token = route.params.token as string

    if (programmeId === undefined || token === undefined) {
      await invalidAuthorization(programmeId, token)
    }

    const authResponse = await authenticate(programmeId, token)

    console.log('Onboarding authenticated. Response: ', authResponse)

    await getContent()
    if (authorized.value === true) {
      initialized.value = true
    } else {
      await invalidAuthorization(programmeId, token)
    }

    if (dictionaries?.value) {
      localizationStore.updateTranslations(dictionaries.value)
    } else {
      console.warn('Dictionaries value is undefined or null')
    }
    const factory = new FactoryBuilder()
    onboardingBasePath.value = await factory.getOnboardingBasePath()
  }

  const invalidAuthorization = async (programmeId: string, token: string) => {
    // Show that we can't authorize the user
    await router.replace({
      name: 'Invalid',
      params: { programmeId: programmeId },
      query: { token: token }
    })
    initialized.value = true
  }

  const authenticate = async (programmeId: string, token: string) => {
    try {
      const onboardingApi = await factory.getOnboardingApi()
      const response = await onboardingApi.authenticate({ programmeId, token })
      authorized.value = response.authorized ?? false
      return response
    } catch (error) {
      console.error('Error fetching onboarding data:', error)
      return error
    }
  }

  const getContent = async () => {
    try {
      const onboardingApi = await factory.getOnboardingApi()
      const response = await onboardingApi.getContent()
      causeOfTreatment.value = response.causeOfTreatment ?? ''
      currentStepType.value = response.currentStep ?? ''
      taeCoverage.value = response.missingProgrammeValues

      if (response.dictionaries) {
        dictionaries.value = response.dictionaries
      }

      businessUnitLogo.value = response.onboardingPage?.businessUnitLogo ?? ''
      customerLogo.value = response.onboardingPage?.customerLogo ?? ''

      if (response.onboardingPage && response.onboardingPage !== undefined) {
        console.log('response.onboardingPage.wizardSteps', response.onboardingPage.wizardSteps)
        steps.value = (
          convertPropertiesToLowercase(response.onboardingPage.wizardSteps) as Partial<IStep>[]
        ).map(
          (step) =>
            ({
              ...step,
              completed: false
            }) as IStep
        )
        t.value = response.dictionaries
      }
    } catch (error) {
      console.error('Error fetching onboarding content:', error)
      throw error
    }
  }

  const updateAllSteps = (newSteps: IStep[]) => {
    steps.value = newSteps
  }
  const setLoadingState = (loadingStateValue: boolean) => {
    loadingState.value = loadingStateValue
  }

  const setActiveStep = (type: string) => {
    if (type === 'ReceiptTae') {
      currentStepType.value = 'ReceiptTae'
      type = 'Receipt'
    } else if (type === 'AtRiskReciept') {
      currentStepType.value = 'AtRiskReciept'
      type = 'Receipt'
    }
    steps.value.map((step: IStep) => {
      if (step.type === type) {
        console.log('Active step', step.type)
        step.active = true
      } else {
        step.active = false
      }
    })
  }

  const currentStep = (): IStep | undefined => {
    return steps.value?.find((step: IStep) => step.active === true) || undefined
  }

  const setRisk = async () => {
    try {
      const risk: boolean = atRisk.value === 'true'
      const onboardingApi = await factory.getOnboardingApi()
      const response = await onboardingApi.setRisk({ risk })

      console.log('At risk status set to:', atRisk.value)
      if (response === true) {
        console.log('At risk status updated successfully')
      } else {
        console.warn('Failed to update at risk status: ', response)
      }
    } catch (error) {
      console.error('Error setting at risk:', error)
      throw error
    }
  }

  return {
    init,
    currentStep,
    updateAllSteps,
    setLoadingState,
    setActiveStep,
    setRisk,
    authorized,
    initialized,
    steps,
    loadingState,
    causeOfTreatment,
    currentStepType,
    taeCoverage,
    dictionaries,
    atRisk,
    customerLogo,
    businessUnitLogo,
    onboardingBasePath
  }
})
